import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormStateContext, UserInitializationContext } from '|/context';
import { currency } from '|/helpers';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { useRefreshDashboardSummary } from '|/hooks/use-dashboard-summary-refresh';
import { useNativePlaid } from './plaid-modal';
import { isDigitalWalletSelection, isPayLaterSelection } from './utils/digital-wallet';
import { usePaymentContext } from '../../contexts/payment';
import { collectPayment, fetchPaymentSummary, submitConsents } from '../../services';
import type { CompletePurchaseSubmitResult } from '../../use-complete-purchase-vm';

export const getPayAtOrderButtonText = (amountDue: number, isPayLater: boolean) =>
  amountDue > 0 && !isPayLater
    ? `Pay ${currency(amountDue).replace('.00', '')} now`
    : 'Place order';

const minimumDigitalWalletAmount = 1;

interface UsePayAtOrderOptions {
  onSubmit: () => Promise<CompletePurchaseSubmitResult>;
  buttonText?: string;
}

export const usePayAtOrder = ({
  onSubmit,
  buttonText: buttonTextOverride
}: UsePayAtOrderOptions) => {
  const {
    state: { paymentSummary },
    dispatch: paymentDispatch
  } = usePaymentContext();
  const {
    shouldDisableWhileImpersonating,
    shouldInitiateRequestForPayment,
    shouldCollectPayment,
    requestForPaymentAmount,
    isCardEligible,
    purchasePaymentBreakdown,
    paymentSelection,
    consents
  } = paymentSummary;

  const { isImpersonating: isUserBeingImpersonated } = useContext(UserInitializationContext);
  const { trackEvent } = useAnalyticsContext();
  const { setSubmitError } = useContext(FormStateContext);
  const refreshDashboardSummary = useRefreshDashboardSummary();

  const [hasCustomerConfirmedSufficientFunds, setHasCustomerConfirmedSufficientFunds] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDigitalWalletSupported, setIsDigitalWalletSupported] = useState(false);
  const [isPlaidModalOpen, setIsPlaidModalOpen] = useState(false);

  const hasDigitalWalletSelection = isDigitalWalletSelection(paymentSelection);
  const shouldUseDigitalWallet =
    hasDigitalWalletSelection && purchasePaymentBreakdown.amountDue >= minimumDigitalWalletAmount;
  const isPayLater = isPayLaterSelection(paymentSelection);
  const isDigitalWalletAvailable = isDigitalWalletSupported && isCardEligible;
  const shouldBlockImpersonatedSubmit = shouldDisableWhileImpersonating && !isPayLater;
  const shouldShowConsents = !shouldUseDigitalWallet || isDigitalWalletAvailable;
  const shouldShowImpersonationText =
    isUserBeingImpersonated && (shouldBlockImpersonatedSubmit || shouldUseDigitalWallet);
  const shouldRequireSufficientFunds =
    purchasePaymentBreakdown.amountDue > 0 &&
    paymentSelection?.selectionType === 'BankAccount' &&
    !!paymentSelection.bankAccount?.accountMask;

  useEffect(() => {
    setHasCustomerConfirmedSufficientFunds(!shouldRequireSufficientFunds);
  }, [shouldRequireSufficientFunds]);

  const isButtonDisabled =
    isSubmitting || !hasCustomerConfirmedSufficientFunds || shouldShowImpersonationText;
  const buttonText =
    buttonTextOverride ?? getPayAtOrderButtonText(purchasePaymentBreakdown.amountDue, isPayLater);
  const consentTypes = useMemo(
    () => consents.map(({ consentType, consentSubType }) => ({ consentType, consentSubType })),
    [consents]
  );

  const handleSubmit = useCallback(async () => {
    await onSubmit();
  }, [onSubmit]);

  const handleError = useCallback(() => {
    setSubmitError(new Error('Failed to collect payment'));
  }, [setSubmitError]);

  const onPlaidExit = useCallback(() => {
    /* re-show PAO inline — no-op since it's already visible */
  }, []);

  const { isNativeApp, openNativePlaid } = useNativePlaid({
    onSuccess: handleSubmit,
    onExit: onPlaidExit,
    onError: handleError
  });

  const openPlaidModal = isNativeApp
    ? openNativePlaid
    : () => {
        setIsPlaidModalOpen(true);
      };

  const onCardAdded = useCallback(async () => {
    const summary = await fetchPaymentSummary();
    paymentDispatch({
      type: 'SET_IS_CARD_ELIGIBLE',
      payload: summary.isCardEligible
    });
    return summary;
  }, [paymentDispatch]);

  const onDigitalWalletPaymentCollected = useCallback(async () => {
    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment,
      shouldCollectPayment,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    const summary = await fetchPaymentSummary();
    paymentDispatch({ type: 'SET_PAYMENT_SUMMARY', payload: summary });

    return onSubmit();
  }, [
    buttonText,
    consentTypes,
    isUserBeingImpersonated,
    onSubmit,
    paymentDispatch,
    paymentSelection,
    purchasePaymentBreakdown,
    requestForPaymentAmount,
    shouldCollectPayment,
    shouldInitiateRequestForPayment,
    trackEvent
  ]);

  const onPurchaseRolledBack = useCallback(() => {
    refreshDashboardSummary();
  }, [refreshDashboardSummary]);

  const submitPayAtOrder = async (): Promise<CompletePurchaseSubmitResult> => {
    setIsSubmitting(true);

    trackEvent('pp_complete_purchase_pao_submit', {
      isUserBeingImpersonated,
      consentTypes,
      shouldInitiateRequestForPayment,
      shouldCollectPayment,
      requestForPaymentAmount,
      purchasePaymentBreakdown,
      paymentSelection,
      buttonText
    });

    try {
      if (isUserBeingImpersonated) {
        if (shouldBlockImpersonatedSubmit) {
          throw new Error('Disabled while impersonating');
        }

        const { didNavigate } = await onSubmit();
        setIsSubmitting(didNavigate);
        return { didNavigate };
      }

      if (shouldCollectPayment) {
        const { paymentCollectionSucceeded } = await collectPayment(consentTypes);

        if (!paymentCollectionSucceeded) {
          refreshDashboardSummary();
          return { didNavigate: true };
        }

        const { didNavigate } = await onSubmit();
        setIsSubmitting(didNavigate);
        return { didNavigate };
      }

      if (consentTypes.length > 0) {
        await submitConsents(consentTypes);
      }

      if (shouldInitiateRequestForPayment) {
        openPlaidModal();
        setIsSubmitting(false);
        return { didNavigate: true };
      }

      const { didNavigate } = await onSubmit();
      setIsSubmitting(didNavigate);
      return { didNavigate };
    } catch {
      setIsSubmitting(false);
      handleError();
      return { didNavigate: false };
    }
  };

  return {
    paymentSummary,
    purchasePaymentBreakdown,
    paymentSelection,
    requestForPaymentAmount,
    isCardEligible,
    isSubmitting,
    isButtonDisabled,
    isDigitalWalletAvailable,
    isDigitalWalletSupported,
    isPlaidModalOpen,
    isUserBeingImpersonated,
    shouldUseDigitalWallet,
    hasDigitalWalletSelection,
    shouldShowConsents,
    shouldShowImpersonationText,
    shouldInitiateRequestForPayment,
    hasCustomerConfirmedSufficientFunds,
    buttonText,
    consentTypes,
    setHasCustomerConfirmedSufficientFunds,
    setIsSubmitting,
    setIsDigitalWalletSupported,
    setIsPlaidModalOpen,
    onCardAdded,
    onDigitalWalletPaymentCollected,
    onPurchaseRolledBack,
    handleError,
    handleSubmit,
    onPlaidExit,
    submitPayAtOrder
  };
};

export type PayAtOrderController = ReturnType<typeof usePayAtOrder>;
